import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import HeadsetLoginQrModal from '../HeadsetLoginQrModal';

const QureHeader = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const profileDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-shellHeader flex h-shellHeaderQure items-center border-b border-slate-200/70 bg-white/80 px-5 shadow-[0_8px_30px_rgba(15,23,42,0.045)] backdrop-blur-xl">
        <div className="flex items-center gap-4">
          <button
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-[1.35rem] text-qure-secondary transition hover:bg-qure-primaryLight hover:text-qure-primary"
            type="button"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle Sidebar"
          >
            <i className="bi bi-list" />
          </button>

          <Link to="/qure-dashboard" className="inline-flex items-center">
            <img
              src="https://qure.science/images/qure.webp"
              alt="Qure"
              className="h-9 w-auto transition-transform hover:scale-[1.03]"
              style={{
                filter:
                  'invert(40%) sepia(95%) saturate(1000%) hue-rotate(165deg) brightness(92%) contrast(95%)',
              }}
            />
          </Link>

          <span className="rounded-full bg-qureGradient px-3 py-1 text-xs font-semibold tracking-wide text-white">
            Qure
          </span>
        </div>
        
        <div className="ml-auto flex items-center gap-4">
          <button
            className="inline-flex items-center gap-2 rounded-xl bg-qureGradient px-4 py-2 text-sm font-medium tracking-[-0.01em] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.28),0_8px_18px_rgba(8,145,178,0.22)] transition hover:-translate-y-0.5 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.28),0_12px_22px_rgba(8,145,178,0.28)]"
            onClick={() => setQrModalOpen(true)}
            type="button"
          >
            <i className="bi bi-qr-code text-[1.1rem]" />
            <span className="hidden sm:inline">Headset Login</span>
          </button>

          <div className="relative z-shellDropdown" ref={profileDropdownRef}>
            <button
              className="inline-flex items-center gap-2 rounded-full px-2 py-1 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-qure-primaryLight"
              type="button"
              onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
            >
              <img
                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                  user?.username || 'User'
                )}&background=17a2b8&color=fff`}
                alt="Profile"
                className="h-10 w-10 rounded-full object-cover ring-2 ring-white shadow-[0_0_0_1px_rgba(8,145,178,0.22)]"
              />
              <span className="hidden text-sm font-medium text-slate-700 sm:inline">
                {user?.username || 'User'}
              </span>
              <i
                className={`bi bi-chevron-down text-slate-500 transition-transform ${
                  profileDropdownOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
            {profileDropdownOpen && (
              <div className="absolute right-0 top-full z-shellDropdown mt-2 min-w-[200px] overflow-hidden rounded-shellSm bg-white py-2 shadow-shellMenu">
                <Link
                  className="flex w-full items-center gap-3 px-5 py-3 text-sm text-slate-800 transition hover:bg-qure-primaryLight hover:text-qure-primary"
                  to="/profile"
                  onClick={() => setProfileDropdownOpen(false)}
                >
                  <i className="bi bi-person w-4 text-center" />
                  Profile
                </Link>
                {(user?.user_id === 79 || user?.user_id === '79' || parseInt(user?.user_id) === 79) && (
                  <Link
                    className="flex w-full items-center gap-3 px-5 py-3 text-sm text-slate-800 transition hover:bg-qure-primaryLight hover:text-qure-primary"
                    to="/admin-dashboard"
                    onClick={() => setProfileDropdownOpen(false)}
                  >
                    <i className="bi bi-shield-check w-4 text-center" />
                    Admin Dashboard
                  </Link>
                )}
                <hr className="my-2 h-px border-0 bg-slate-200" />
                <button
                  className="flex w-full items-center gap-3 px-5 py-3 text-left text-sm text-slate-800 transition hover:bg-qure-primaryLight hover:text-qure-primary"
                  onClick={handleLogout}
                  type="button"
                >
                  <i className="bi bi-box-arrow-right w-4 text-center" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <HeadsetLoginQrModal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        qrApiPath="/qure/profile/qr-code"
        title="Headset Login"
        accentClass="text-qure-secondary"
        closeHoverClass="hover:text-qure-danger"
      />

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-shellHeaderQure z-shellSidebar h-[calc(100vh-64px)] w-qureSidebar overflow-y-auto border-r border-slate-200/80 bg-white/90 py-4 transition-transform lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        id="qureSidebar"
      >
        <nav>
          <Link 
            to="/qure-dashboard" 
            className={`flex items-center gap-3 px-5 py-3 text-sm font-medium transition ${
              isActive('/qure-dashboard')
                ? 'border-l-4 border-qure-primary bg-qure-primaryLight font-semibold text-qure-primary'
                : 'border-l-4 border-transparent text-slate-600 hover:border-qure-primary hover:bg-qure-primaryLight hover:text-qure-primary'
            }`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-house w-5 text-center text-[1.05rem]" />
            Dashboard
          </Link>
          <Link 
            to="/my-teams" 
            className={`flex items-center gap-3 px-5 py-3 text-sm font-medium transition ${
              isActive('/my-teams')
                ? 'border-l-4 border-qure-primary bg-qure-primaryLight font-semibold text-qure-primary'
                : 'border-l-4 border-transparent text-slate-600 hover:border-qure-primary hover:bg-qure-primaryLight hover:text-qure-primary'
            }`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-people w-5 text-center text-[1.05rem]" />
            My Teams
          </Link>

          <div className="px-5 pb-2 pt-5 text-[0.7rem] font-bold uppercase tracking-widest text-slate-400">
            Experiments
          </div>
          <Link 
            to="/qure-calendar" 
            className={`flex items-center gap-3 px-5 py-3 text-sm font-medium transition ${
              isActive('/qure-calendar')
                ? 'border-l-4 border-qure-primary bg-qure-primaryLight font-semibold text-qure-primary'
                : 'border-l-4 border-transparent text-slate-600 hover:border-qure-primary hover:bg-qure-primaryLight hover:text-qure-primary'
            }`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-calendar-check w-5 text-center text-[1.05rem]" />
            Calendar
          </Link>
          <Link 
            to="/qure-analytics" 
            className={`flex items-center gap-3 px-5 py-3 text-sm font-medium transition ${
              isActive('/qure-analytics')
                ? 'border-l-4 border-qure-primary bg-qure-primaryLight font-semibold text-qure-primary'
                : 'border-l-4 border-transparent text-slate-600 hover:border-qure-primary hover:bg-qure-primaryLight hover:text-qure-primary'
            }`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-graph-up w-5 text-center text-[1.05rem]" />
            Experiment Analytics
          </Link>

          <div className="px-5 pb-2 pt-5 text-[0.7rem] font-bold uppercase tracking-widest text-slate-400">
            Support
          </div>
          <Link 
            to="/help-desk" 
            className={`flex items-center gap-3 px-5 py-3 text-sm font-medium transition ${
              isActive('/help-desk')
                ? 'border-l-4 border-qure-primary bg-qure-primaryLight font-semibold text-qure-primary'
                : 'border-l-4 border-transparent text-slate-600 hover:border-qure-primary hover:bg-qure-primaryLight hover:text-qure-primary'
            }`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-headset w-5 text-center text-[1.05rem]" />
            Help Desk
          </Link>
          <Link 
            to="/support" 
            className={`flex items-center gap-3 px-5 py-3 text-sm font-medium transition ${
              isActive('/support')
                ? 'border-l-4 border-qure-primary bg-qure-primaryLight font-semibold text-qure-primary'
                : 'border-l-4 border-transparent text-slate-600 hover:border-qure-primary hover:bg-qure-primaryLight hover:text-qure-primary'
            }`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-headset w-5 text-center text-[1.05rem]" />
            Support Ticket
          </Link>
        </nav>
      </aside>

    </>
  );
};

export default QureHeader;
